import type { IsoDate, IsoDateTime } from './common';
import { CHANNEL_NAMES, type IntegratedChannel } from './marketplace';
import { WEEKDAY_SHORT_LABELS } from './performance';

// Zamanında kargo, spelled out: an order is on time when it was handed to the carrier (shipped_at) by
// its deadline (ship_by, "son kargo zamanı"). The deadline is the marketplace's own where it gives one,
// the warehouse's rule, or the earlier of the two (the basis), and the rule is the warehouse's to set:
// the cut-off, the working days, the time of day a deadline falls on and the days it is closed.

/** Pazaryeri siparişlerinde esas: the deadline an order of a marketplace that gives its own has to meet. */
export const SHIP_BY_BASES = ['marketplace', 'warehouse', 'earliest'] as const;
export type ShipByBasis = (typeof SHIP_BY_BASES)[number];

export const SHIP_BY_BASIS_LABELS: Readonly<Record<ShipByBasis, string>> = {
  marketplace: 'Pazaryerinin verdiği süre',
  warehouse: 'Depo kuralı',
  earliest: 'Hangisi daha erkense',
};

/**
 * Channels whose orders come with the marketplace's own deadline (Trendyol and n11 agreedDeliveryDate,
 * Hepsiburada dueDate, Koçtaş and Teknosa shipping_deadline, Amazon LatestShipDate, İdefix
 * lastShipmentDate, Pazarama estimatedShippingDate). PttAVM, Çiçeksepeti and Shopify give none, and
 * manual orders never have one: they follow the warehouse rule.
 */
export const SHIP_BY_CHANNELS: readonly IntegratedChannel[] = ['trendyol', 'hepsiburada', 'amazon', 'n11', 'pazarama', 'idefix', 'koctas', 'teknosa'];

/** A date the warehouse ships nothing: bayram, resmî tatil, sayım günü… */
export interface ClosedDay {
  readonly date: IsoDate;
  readonly note: string | null;
}

/** The definition of on time of a warehouse. */
export interface ShipByRule {
  /** "14:00": in by it on a working day, out the same day */
  readonly cutoff: string;
  /** ISO weekdays the warehouse ships, 1 = Monday … 7 = Sunday */
  readonly workdays: readonly number[];
  /** "23:59": the time of day a deadline falls on (son kargo saati), to the end of that minute */
  readonly deadline: string;
  /** days it ships nothing, oldest first */
  readonly closedDays: readonly ClosedDay[];
  readonly basis: ShipByBasis;
}

export const DEFAULT_SHIP_BY_RULE: ShipByRule = { cutoff: '14:00', workdays: [1, 2, 3, 4, 5, 6], deadline: '23:59', closedDays: [], basis: 'marketplace' };

/**
 * Where a change of the definition was made: the panel (a depo yöneticisi, "Tanımı değiştir") or the
 * server command (`ship-by:holidays`, nobody signed in). The check of ship_by_rule_changes.changed_via (0060).
 */
export const SHIP_BY_CHANGE_SOURCES = ['panel', 'cli'] as const;
export type ShipByChangeSource = (typeof SHIP_BY_CHANGE_SOURCES)[number];

/** The definition's last change: when, by whom and from where (the warehouse side only), and whether closed orders were recomputed too. */
export interface ShipByRuleChange {
  readonly at: IsoDateTime;
  readonly by: string | null;
  readonly via: ShipByChangeSource | null;
  readonly appliedToHistory: boolean;
}

/** Who made a change, as the card names them after "Son değişiklik": the person, or "sunucu komutuyla" */
export function shipByChangeAuthor(change: ShipByRuleChange): string | null {
  return change.via === 'cli' ? 'sunucu komutuyla' : change.by;
}

export interface ShipByDefinition {
  readonly warehouseId: string;
  readonly warehouseCode: string;
  readonly timezone: string;
  readonly rule: ShipByRule;
  /** the channels of the stores this warehouse ships for (a seller: its own) whose marketplaces give a deadline */
  readonly channels: readonly string[];
  readonly lastChange: ShipByRuleChange | null;
}

export interface ShipByRuleInput {
  readonly warehouseId: string;
  readonly rule: ShipByRule;
  /** "Geçmiş siparişlere de uygula": closed orders get the new deadline too, so past figures follow the new definition */
  readonly applyToHistory: boolean;
}

/** What a new definition changes in a warehouse's orders. */
export interface ShipByImpact {
  /** open orders (not shipped, delivered, cancelled or returned), and of them those whose deadline changes */
  readonly open: number;
  readonly openChanged: number;
  /** of those, orders whose new deadline has passed already while the old one had not */
  readonly openNowLate: number;
  /**
   * of those, orders whose old or new deadline has passed: Performans counts an order in the period of its
   * deadline, so these move the figures of past periods whether or not closed orders are recomputed
   */
  readonly openPastChanged: number;
  /** closed orders, and of them those whose deadline changes when they are recomputed too */
  readonly closed: number;
  readonly closedChanged: number;
  /** orders handed to the carrier that count on time instead of late, and late instead of on time */
  readonly becameOnTime: number;
  readonly becameLate: number;
}

export interface ShipByRuleSaved {
  readonly definition: ShipByDefinition;
  /** what was recomputed: the closed orders' figures only with applyToHistory */
  readonly impact: ShipByImpact;
  readonly appliedToHistory: boolean;
}

export const WEEKDAY_NAMES = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'] as const;

/** A closed day's note is short: "Kurban Bayramı", "Yıllık sayım" */
export const CLOSED_DAY_NOTE_MAX = 120;
/** Years of bayrams fit; more is a mistake */
export const CLOSED_DAYS_MAX = 500;

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** "2026-10-29" is a day of the calendar */
export function isCalendarDay(day: string): boolean {
  if (!DAY.test(day)) return false;
  const parsed = new Date(`${day}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === day;
}

/** The rule as it is stored: weekdays once each in order, closed days by date, notes trimmed (empty: none). */
export function normalShipByRule(rule: ShipByRule): ShipByRule {
  const byDate = new Map<string, ClosedDay>();
  for (const d of rule.closedDays) byDate.set(d.date, { date: d.date, note: d.note?.trim() ? d.note.trim() : null });
  return {
    cutoff: rule.cutoff,
    workdays: [...new Set(rule.workdays)].sort((a, b) => a - b),
    deadline: rule.deadline,
    closedDays: [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date)),
    basis: rule.basis,
  };
}

/** Why a rule cannot be saved (Turkish, for the person), or null. */
export function shipByRuleProblem(rule: ShipByRule): string | null {
  if (!HHMM.test(rule.cutoff)) return 'Kesim saati SS:DD biçiminde olmalı';
  if (!HHMM.test(rule.deadline)) return 'Son kargo saati SS:DD biçiminde olmalı';
  if (rule.deadline <= rule.cutoff) return 'Son kargo saati kesim saatinden sonra olmalı';
  if (rule.workdays.length === 0) return 'En az bir çalışma günü seçin';
  if (rule.workdays.some((d) => !Number.isInteger(d) || d < 1 || d > 7)) return 'Çalışma günleri 1 (Pazartesi) ile 7 (Pazar) arasında olmalı';
  if (!SHIP_BY_BASES.includes(rule.basis)) return 'Pazaryeri siparişlerinde esas seçilmeli';
  if (rule.closedDays.length > CLOSED_DAYS_MAX) return `En fazla ${CLOSED_DAYS_MAX} kapalı gün girilebilir`;
  const seen = new Set<string>();
  for (const d of rule.closedDays) {
    if (!isCalendarDay(d.date)) return `Kapalı gün okunamadı: ${d.date}`;
    if (seen.has(d.date)) return `${dayText(d.date)} iki kez girilmiş`;
    seen.add(d.date);
    if ((d.note?.trim().length ?? 0) > CLOSED_DAY_NOTE_MAX) return `Kapalı gün notu en fazla ${CLOSED_DAY_NOTE_MAX} karakter olabilir`;
  }
  return null;
}

// --- the words -----------------------------------------------------------------------------------

const UNIT_DATIVE = ['', "'e", "'ye", "'e", "'e", "'e", "'ya", "'ye", "'e", "'a"] as const;
// sıfır, on, yirmi, otuz, kırk, elli
const TENS_DATIVE = ["'a", "'a", "'ye", "'a", "'a", "'ye"] as const;

/** A time with the dative ending it is read with: "14:00'e", "17:00'ye", "23:59'a", "16:30'a" */
export function timeTo(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  // read as its minutes, or as its hour on the hour
  const n = m > 0 ? m : h;
  return `${hhmm}${n % 10 !== 0 ? UNIT_DATIVE[n % 10] : TENS_DATIVE[Math.floor(n / 10)]}`;
}

/** "Cumartesi ve Pazar", "Salı, Perşembe ve Pazar" */
export function joinAnd(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} ve ${items[items.length - 1]}`;
}

/** "29.10.2026" */
export const dayText = (day: IsoDate) => `${day.slice(8, 10)}.${day.slice(5, 7)}.${day.slice(0, 4)}`;

const nextDay = (day: IsoDate) => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

/** "Pzt–Cum (Cumartesi ve Pazar çalışılmıyor)", "her gün (Cumartesi ve Pazar dahil)" */
export function workdaysText(workdays: readonly number[]): string {
  const days = [...new Set(workdays)].sort((a, b) => a - b);
  if (days.length === 7) return 'her gün (Cumartesi ve Pazar dahil)';
  const first = days[0]!;
  const last = days[days.length - 1]!;
  const run = days.length >= 3 && last - first === days.length - 1;
  const on = run ? `${WEEKDAY_SHORT_LABELS[first - 1]}–${WEEKDAY_SHORT_LABELS[last - 1]}` : days.map((d) => WEEKDAY_SHORT_LABELS[d - 1]).join(', ');
  const off = [1, 2, 3, 4, 5, 6, 7].filter((d) => !days.includes(d)).map((d) => WEEKDAY_NAMES[d - 1]!);
  return `${on} (${joinAnd(off)} çalışılmıyor)`;
}

export interface ClosedDayGroup {
  readonly from: IsoDate;
  readonly to: IsoDate;
  readonly note: string | null;
}

/** Consecutive closed days with the same note as one stretch: a bayram of four days is one line. */
export function closedDayGroups(days: readonly ClosedDay[]): ClosedDayGroup[] {
  const groups: { from: IsoDate; to: IsoDate; note: string | null }[] = [];
  for (const d of [...days].sort((a, b) => a.date.localeCompare(b.date))) {
    const last = groups[groups.length - 1];
    if (last && nextDay(last.to) === d.date && last.note === d.note) last.to = d.date;
    else groups.push({ from: d.date, to: d.date, note: d.note });
  }
  return groups;
}

/** "29.10.2026 (Cumhuriyet Bayramı)", "26.05–29.05.2027 (Kurban Bayramı)", "31.12.2026–01.01.2027" */
export function closedDayGroupText(g: ClosedDayGroup): string {
  const days =
    g.from === g.to
      ? dayText(g.from)
      : g.from.slice(0, 4) === g.to.slice(0, 4)
        ? `${dayText(g.from).slice(0, 5)}–${dayText(g.to)}`
        : `${dayText(g.from)}–${dayText(g.to)}`;
  return g.note ? `${days} (${g.note})` : days;
}

/** The upcoming closed stretches a definition names; the rest is counted */
const CLOSED_SHOWN = 4;

/** "Trendyol ve Hepsiburada": the channels given that bring their own deadline, in a fixed order */
function deadlineChannels(channels: readonly string[]): string {
  return joinAnd(SHIP_BY_CHANNELS.filter((c) => channels.includes(c)).map((c) => CHANNEL_NAMES[c] ?? c));
}

/**
 * The definition in plain words, sentence by sentence: what counts as handed over, whose deadline an
 * order has, the warehouse rule, the working days and the closed days to come (from `today`, the
 * warehouse's day), or the last past one when none is to come. `channels`: the stores' marketplaces that
 * give a deadline (SHIP_BY_CHANNELS).
 */
export function shipByDefinitionText(rule: ShipByRule, channels: readonly string[], today: IsoDate): string[] {
  const named = deadlineChannels(channels);
  const sentences = [
    'Sipariş son kargo zamanına kadar kargo firmasına verildiyse zamanında sayılır (Sevkiyat masasındaki okutma; okutulmadan giden kolide kargo firmasının ya da pazaryerinin “kargoda” bilgisinin depoya geldiği an).',
  ];
  if (rule.basis === 'warehouse') {
    sentences.push('Son kargo zamanı her siparişte depo kuralıyla belirlenir; pazaryerinin verdiği süreye bakılmaz.');
  } else if (rule.basis === 'earliest') {
    sentences.push(
      named
        ? `Son kargo zamanı: ${named} siparişlerinde pazaryerinin verdiği süre ile depo kuralından hangisi daha erkense; diğerlerinde depo kuralı.`
        : 'Son kargo zamanı: pazaryerinin verdiği süre ile depo kuralından hangisi daha erkense; süre vermeyen siparişlerde depo kuralı.',
    );
  } else {
    sentences.push(
      named
        ? `Son kargo zamanı: ${named} siparişlerinde pazaryerinin verdiği süre; diğerlerinde depo kuralı.`
        : 'Son kargo zamanı: pazaryeri süre veriyorsa o süre; vermiyorsa depo kuralı.',
    );
  }
  sentences.push(
    `Depo kuralı: çalışma gününde ${timeTo(rule.cutoff)} kadar gelen sipariş aynı gün ${timeTo(rule.deadline)} kadar, sonra gelen bir sonraki çalışma günü ${timeTo(rule.deadline)} kadar kargoya verilmeli.`,
  );
  sentences.push(`Çalışma günleri: ${workdaysText(rule.workdays)}.`);
  const ahead = closedDayGroups(rule.closedDays.filter((d) => d.date >= today));
  const past = closedDayGroups(rule.closedDays.filter((d) => d.date < today));
  if (ahead.length > 0) {
    const shown = ahead.slice(0, CLOSED_SHOWN).map(closedDayGroupText).join(', ');
    const more = ahead.length > CLOSED_SHOWN ? ` ve ${ahead.length - CLOSED_SHOWN} dönem daha` : '';
    // closed days move the rule's deadline only: a marketplace's own stays where it is (app.effective_ship_by)
    const kept = rule.basis !== 'warehouse' && named ? ' (pazaryerinin verdiği süre değişmez)' : '';
    sentences.push(`Kapalı günler: ${shown}${more}; bu günlerde kargo çıkmaz, depo kuralındaki süre sonraki çalışma gününe kayar${kept}.`);
  } else if (past.length > 0) {
    // the past ones still shaped the deadlines of their days
    const last = past[past.length - 1]!;
    sentences.push('İleride kapalı gün girilmemiş: resmî tatil ve bayramlar, girilmedikçe çalışma günü sayılır.');
    sentences.push(`Geçmişteki son kapalı ${last.from === last.to ? 'gün' : 'günler'}: ${closedDayGroupText(last)}.`);
  } else {
    sentences.push('Kapalı gün girilmemiş: resmî tatil ve bayramlar, girilmedikçe çalışma günü sayılır.');
  }
  return sentences;
}

/** What a new definition changes, line by line, for the confirmation ("Kesim saati 14:00 → 15:00"). */
export function shipByRuleChanges(before: ShipByRule, after: ShipByRule): string[] {
  const changes: string[] = [];
  if (before.cutoff !== after.cutoff) changes.push(`Kesim saati ${before.cutoff} → ${after.cutoff}`);
  if (before.deadline !== after.deadline) changes.push(`Son kargo saati ${before.deadline} → ${after.deadline}`);
  for (let day = 1; day <= 7; day++) {
    const was = before.workdays.includes(day);
    const is = after.workdays.includes(day);
    if (was !== is) changes.push(`${WEEKDAY_NAMES[day - 1]} ${is ? 'artık çalışılıyor' : 'artık çalışılmıyor'}`);
  }
  if (before.basis !== after.basis) changes.push(`Pazaryeri siparişlerinde esas: ${SHIP_BY_BASIS_LABELS[before.basis]} → ${SHIP_BY_BASIS_LABELS[after.basis]}`);
  const was = new Map(before.closedDays.map((d) => [d.date, d.note]));
  const is = new Map(after.closedDays.map((d) => [d.date, d.note]));
  const added = after.closedDays.filter((d) => !was.has(d.date));
  const removed = before.closedDays.filter((d) => !is.has(d.date));
  const renamed = after.closedDays.filter((d) => was.has(d.date) && (was.get(d.date) ?? null) !== d.note);
  for (const g of closedDayGroups(added)) changes.push(`Kapalı gün eklendi: ${closedDayGroupText(g)}`);
  for (const g of closedDayGroups(removed)) changes.push(`Kapalı gün kaldırıldı: ${closedDayGroupText(g)}`);
  for (const g of closedDayGroups(renamed)) changes.push(`Kapalı gün notu: ${closedDayGroupText(g)}`);
  return changes;
}

const count = (n: number) => n.toLocaleString('tr-TR');

/** What saving does to the orders, for the confirmation; `applyToHistory`: the box "Geçmiş siparişlere de uygula". */
export function shipByImpactText(impact: ShipByImpact, applyToHistory: boolean): string[] {
  const lines: string[] = [];
  if (impact.open === 0) lines.push('Açık sipariş yok.');
  else if (impact.openChanged === 0) lines.push(`Açık siparişlerin (${count(impact.open)}) son kargo zamanı değişmez.`);
  else lines.push(`${count(impact.openChanged)} açık siparişin son kargo zamanı değişir (toplam ${count(impact.open)} açık sipariş).`);
  if (impact.openNowLate > 0) lines.push(`${count(impact.openNowLate)} açık sipariş yeni tanıma göre şimdiden gecikmiş sayılır.`);
  // Performans counts an order in the period of its deadline: an open one due already, by the old or the
  // new definition, moves the figures of past periods even when closed orders keep theirs
  const past =
    impact.openPastChanged > 0
      ? `${count(impact.openPastChanged)} açık siparişin eski ya da yeni son kargo zamanı geçmişte: geçmiş dönemlerin Performans rakamları bu siparişler kadar değişir.`
      : 'Geçmiş dönemlerin Performans rakamları değişmez.';
  if (impact.closed === 0) {
    lines.push('Kapanmış sipariş yok.');
    if (impact.openPastChanged > 0) lines.push(past);
    return lines;
  }
  if (!applyToHistory) {
    lines.push(
      `Kapalı siparişlerin (kargoya verilmiş, teslim edilmiş, iptal, iade) son kargo zamanı ve zamanında / geç durumu değişmez.${
        impact.closedChanged > 0 ? ` Geçmişe de uygulansaydı ${count(impact.closedChanged)} kapalı siparişin son kargo zamanı değişirdi.` : ''
      }`,
      past,
    );
    return lines;
  }
  if (impact.closedChanged === 0) {
    lines.push('Kapalı siparişlerin son kargo zamanı değişmez.', past);
    return lines;
  }
  const flips = [
    impact.becameOnTime > 0 ? `${count(impact.becameOnTime)} sipariş geç yerine zamanında` : '',
    impact.becameLate > 0 ? `${count(impact.becameLate)} sipariş zamanında yerine geç` : '',
  ].filter(Boolean);
  lines.push(
    `${count(impact.closedChanged)} kapalı siparişin son kargo zamanı değişir (toplam ${count(impact.closed)} kapalı sipariş)${
      flips.length > 0 ? `: ${joinAnd(flips)} sayılır` : '; hiçbirinin zamanında / geç durumu değişmez'
    }. Geçmiş Performans rakamları yeni tanıma göre sayılır.`,
  );
  return lines;
}
